import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import PropertyDetails from "../components/PropertyDetails";
import PropertyFeatures from "../components/PropertyFeatures";
import RentalValueAnalysis from "../components/RentalValueAnalysis";
import ComparablesSection from "../components/ComparablesSection";
import ImageGallery from "../components/ImageGallery";
import OutOfPocketSection from "../components/OutOfPocketSection";
import AIRentSummary from "../components/AIRentSummary";
import useIsNarrow from "../hooks/useIsNarrow";
import { calculateClosingCosts } from "../utils/closingCostsCalculator";
import { resolveMortgageRates } from "../utils/rentalAnalysisCalculator";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function ClientViewPage() {
  const { token } = useParams();
  // This page is sent to clients by email and is opened on a phone more often than not.
  const narrow = useIsNarrow();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadData = async () => {
    try {
      const resp = await fetch(`${API_URL}/client/share/${token}`);
      if (!resp.ok) {
        if (resp.status === 410) throw new Error("This link has expired");
        if (resp.status === 404) throw new Error("Link not found");
        throw new Error("Failed to load");
      }
      const json = await resp.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: "center", marginTop: "100px", color: "#666" }}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: "center", marginTop: "100px" }}>
          <h2 style={{ color: "#d32f2f" }}>{error}</h2>
          <p style={{ color: "#666" }}>Please contact your representative for a new link.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={{
        backgroundColor: "#fff",
        padding: narrow ? "14px 16px" : "16px 30px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        marginBottom: narrow ? "16px" : "24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: narrow ? "flex-start" : "center",
        flexDirection: narrow ? "column" : "row",
        gap: narrow ? "6px" : 0,
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: narrow ? "19px" : "22px", color: "#333" }}>Investment Opportunities</h1>
          {data.client_name && (
            <p style={{ margin: "4px 0 0", fontSize: "14px", color: "#666" }}>
              Prepared for {data.client_name}
            </p>
          )}
        </div>
        <div style={{ fontSize: "12px", color: "#999" }}>
          Clear Sky Investment Group
        </div>
      </div>

      {/* Listings */}
      <div style={{ padding: narrow ? "0 12px" : "0 30px", maxWidth: "1200px", margin: "0 auto" }}>
        {data.listings.map((listing) => (
          <ClientListingView key={listing.id} listing={listing} />
        ))}
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", padding: "40px 0", color: "#999", fontSize: "12px" }}>
        Clear Sky Investment Group
      </div>
    </div>
  );
}


function ClientListingView({ listing }) {
  const l = listing;
  const narrow = useIsNarrow();

  // Financial controls state. Seeded below from what the analyst actually used for THIS
  // listing, then owned by the client — these are the inputs they are invited to play with.
  const [downPaymentPct, setDownPaymentPct] = useState(l.analysis?.down_payment_percent ?? 25);
  const [piRate, setPiRate] = useState(6.25);
  const [ioRate, setIoRate] = useState(5.75);

  // Open on the analyst's numbers.
  //
  // Both defaults above were hardcoded, so a listing the analyst had priced at a custom rate
  // showed the client a different cap rate, cash flow and IRR than the analyst was quoting —
  // and even with no override the page ignored the global rate in Settings. resolveMortgageRates
  // is the same helper the Rental Value Analysis uses, so "the analyst's rate" means exactly
  // what it means on /predict; /config/mortgage is a public route, so this page can read it.
  //
  // Seeded ONCE. Re-running on every render would stamp on the client's own edits.
  const [ratesSeeded, setRatesSeeded] = useState(false);
  useEffect(() => {
    if (ratesSeeded) return;
    let cancelled = false;
    (async () => {
      let globalConfig = null;
      try {
        const resp = await fetch(`${API_URL}/config/mortgage`);
        if (resp.ok) globalConfig = (await resp.json())?.data || null;
      } catch (e) {
        // Fall through: resolveMortgageRates supplies 6.25/5.75 when it gets nothing.
        console.error("Could not load mortgage config:", e);
      }
      if (cancelled) return;
      const resolved = resolveMortgageRates(globalConfig, l.analysis);
      setPiRate(resolved.piRate);
      setIoRate(resolved.ioRate);
      setRatesSeeded(true);
    })();
    return () => { cancelled = true; };
  }, [l.analysis, ratesSeeded]);

  // Closing costs, when the analyst never saved a figure for this listing.
  //
  // /predict computes a default in exactly this situation; without the same fallback here the
  // client's Total Out of Pocket would read as down payment alone and UNDERSTATE what the deal
  // actually costs them. We call the same calculator on the same config rather than
  // reimplementing it — /config/closing_costs is a public route, so this page can reach it.
  const [defaultClosingCosts, setDefaultClosingCosts] = useState(null);
  const savedClosingCosts = l.analysis?.net_closing_costs;
  const needsClosingCosts = !(typeof savedClosingCosts === "number" && savedClosingCosts > 0);

  useEffect(() => {
    if (!needsClosingCosts || !l.price) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/config/closing_costs?property_id=${l.id}`);
        if (!resp.ok) return;
        const json = await resp.json();
        if (!json?.data || cancelled) return;
        const { netClosingCosts } = calculateClosingCosts({
          askingPrice: l.price,
          buildingType: l.building_type || "Condo",
          saleType: "Resale",
          financing: "Yes",
          config: json.data,
          closingCostCredit: 0,
        });
        setDefaultClosingCosts(netClosingCosts);
      } catch (e) {
        // Leave it unset — the box then shows $0 rather than a wrong number.
        console.error("Could not compute default closing costs:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [l.id, l.price, l.building_type, needsClosingCosts]);

  // Build mortgage config from client controls
  const mortgageConfig = {
    down_payment_percent: downPaymentPct,
    rates: {
      pi: { "30_year_fixed": piRate },
      io: { "7_1_arm": ioRate },
    },
  };

  const downPaymentAmt = Math.round((l.price || 0) * (downPaymentPct / 100));

  // The client page shows the AI prediction ONLY — never the ML number. Both travel in the
  // payload (the email template still reads the ML pair), so the choice is made here, once,
  // and every box below is driven from `aiRent`: Property Details' cap rate and IRR, the
  // rental analysis, and the summary block. Falling back to the ML rent when the AI has not
  // run yet is deliberate — a blank page is worse than a slightly older number.
  const aiRent = l.ai_predicted_rent || l.predicted_rent || 0;

  // The team's saved out-of-pocket inputs, with the client's own down-payment choice layered
  // on top so this page's Financial Assumptions control actually drives the Out of Pocket box.
  // NOTE the three nulls. The analyst's rate is used to SEED the controls above, and must not
  // also reach the calculator: resolveMortgageRates treats a matching override as authoritative,
  // so leaving these in would pin the rate and make the client's own rate inputs inert — they
  // would type a new rate and watch nothing move. Seeding sets the starting point; the controls
  // own it from there, and they feed the calculator through `mortgageConfig` below.
  const propertyAnalysis = {
    ...(l.analysis || {}),
    mortgage_rate: null,
    payment_type: null,
    mortgage_term: null,
    net_closing_costs: needsClosingCosts ? (defaultClosingCosts || 0) : savedClosingCosts,
    down_payment_percent: downPaymentPct,
    // down_payment_amount would pin the figure to whatever the analyst last saved and ignore
    // the control above it, so it is left out on purpose.
  };

  // Build property object matching what PropertyDetails expects
  const property = {
    address: l.address,
    unit_number: l.unit_number,
    price: l.price,
    asking_price: l.price,
    bedrooms: l.bedrooms,
    bathrooms: l.bathrooms,
    sqft: l.sqft,
    monthly_hoa: l.monthly_hoa || 0,
    monthly_tax: l.monthly_tax || 0,
    neighborhood: l.neighborhood,
    borough: l.borough,
    property_type: l.property_type,
    listed_date: l.listed_at,
    listing_url: l.listing_url,
    price_change: l.price_change,
    price_change_pct: l.price_change_pct,
    image_urls: l.image_urls || [],
    floor_plan_url: l.floor_plan_url,
    video_urls: l.video_urls || [],
    tour_urls: l.tour_urls || [],
    floor: l.floor,
    washer_dryer: l.washer_dryer,
    dishwasher: l.dishwasher,
    renovation: l.renovation,
    views: l.views,
    neighborhood_avg_cap_rate: l.neighborhood_avg_cap_rate,
    neighborhood_weighted_avg_cap_rate: l.neighborhood_weighted_avg_cap_rate,
    neighborhood_median_cap_rate: l.neighborhood_median_cap_rate,
    floor_line: l.floor_line,
    building_type: l.building_type,
    home_features: l.home_features || [],
  };

  const prediction = {
    predicted_rent: aiRent,
  };

  // low == high == the AI rent: with a single prediction there is no low/high pair to show,
  // and calculateRentalAnalysis derives its headline cap rate and IRR from highRent.
  const computedValues = {
    originalPrice: l.price,
    discountPct: 0,
    discountedPrice: l.price,
    mlRent: aiRent,
    agentRent: aiRent,
    lowRent: aiRent,
    highRent: aiRent,
  };

  return (
    <div style={{ marginBottom: "40px" }}>
      {/* Property title */}
      <h2 style={{
        fontSize: narrow ? "17px" : "20px",
        fontWeight: "600",
        color: "#333",
        marginBottom: "16px",
        paddingBottom: "8px",
        borderBottom: "2px solid #000",
        // A long address is one unbroken token to the layout engine; without this it is what
        // pushes the whole card wider than the screen.
        overflowWrap: "anywhere",
      }}>
        {l.listing_url ? (
          <a href={l.listing_url} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", textDecoration: "none" }}>
            {(l.address || "").trim()} #{l.unit_number}
          </a>
        ) : (
          `${(l.address || "").trim()} #${l.unit_number}`
        )}
      </h2>

      {/* Image Gallery */}
      <ImageGallery
        imageUrls={l.image_urls || []}
        floorPlanUrl={l.floor_plan_url}
        videoUrls={l.video_urls || []}
        tourUrls={l.tour_urls || []}
      />

      {/* Property Details — same component and same shape as /predict, so the client sees
          the analyst's page minus the internal controls. propertyAnalysis is the saved one
          (not {}), which is what makes cap rate and IRR here match the analyst's figures. */}
      <PropertyDetails
        property={property}
        prediction={prediction}
        propertyAnalysis={propertyAnalysis}
        checklistData={{}}
        computedValues={computedValues}
        showViews={false}
        labelRenovation
      />

      {/* Home Features. PropertyFeatures also renders Building Features and the listing
          description, but the payload carries neither, so only the Home Features box shows. */}
      <PropertyFeatures property={property} />

      {/* Financial Controls */}
      <div style={{
        backgroundColor: "white",
        padding: narrow ? "16px" : "20px",
        borderRadius: "8px",
        border: "2px solid #000",
        marginBottom: "20px",
      }}>
        <div style={{ fontSize: "18px", fontWeight: "700", marginBottom: "16px", color: "#333" }}>
          Financial Assumptions
        </div>
        <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: narrow ? "12px" : "16px", alignItems: "end" }}>
          <div>
            <label style={controlLabelStyle}>Down Payment (%)</label>
            <input
              type="number"
              style={controlInputStyle}
              value={downPaymentPct}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setDownPaymentPct(Math.max(0, Math.min(100, v)));
              }}
              min="0" max="100" step="1"
            />
          </div>
          <div>
            <label style={controlLabelStyle}>Down Payment ($)</label>
            <div style={{ ...controlInputStyle, backgroundColor: "#f8f9fa", display: "flex", alignItems: "center" }}>
              ${downPaymentAmt.toLocaleString()}
            </div>
          </div>
          <div>
            <label style={controlLabelStyle}>P&I Rate (%)</label>
            <input
              type="number"
              style={controlInputStyle}
              value={piRate}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setPiRate(Math.max(0, Math.min(15, v)));
              }}
              min="0" max="15" step="0.125"
            />
          </div>
          <div>
            <label style={controlLabelStyle}>I/O Rate (%)</label>
            <input
              type="number"
              style={controlInputStyle}
              value={ioRate}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setIoRate(Math.max(0, Math.min(15, v)));
              }}
              min="0" max="15" step="0.125"
            />
          </div>
        </div>
      </div>

      {/* Rental Value Analysis */}
      <RentalValueAnalysis
        property={property}
        prediction={prediction}
        propertyAnalysis={propertyAnalysis}
        mlRent={aiRent}
        agentRent={aiRent}
        lowRent={aiRent}
        highRent={aiRent}
        discountedPrice={l.price}
        mortgageConfig={mortgageConfig}
      />

      {/* Out of Pocket — read-only: the renovation budget and timeline are the analyst's
          assumptions, and there is nowhere for a client's edits to be saved. */}
      <OutOfPocketSection
        property={property}
        prediction={prediction}
        propertyAnalysis={propertyAnalysis}
        checklistData={{}}
        onUpdate={() => {}}
        readOnly
      />

      {/* AI rent: the number, the range, the reasoning — nothing else. Sits directly above
          the comps because the reasoning argues from them by name ("Anchored to COMPS 1 & 2
          ..."), so the reader meets the argument and its evidence together. */}
      <AIRentSummary
        predictedRent={l.ai_predicted_rent}
        rentLow={l.ai_rent_low}
        rentHigh={l.ai_rent_high}
        reasoning={l.ai_reasoning}
      />

      {/* Comparables */}
      <ComparablesSection comparables={l.comps || []} />
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh",
  backgroundColor: "#f5f5f5",
  fontFamily: "Arial, sans-serif",
};

const controlLabelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: "600",
  color: "#555",
  marginBottom: "6px",
};

const controlInputStyle = {
  width: "100%",
  padding: "8px 10px",
  border: "2px solid #ddd",
  borderRadius: "6px",
  fontSize: "15px",
  fontWeight: "600",
  boxSizing: "border-box",
};
